import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "primereact/button";
import styles from "./index.module.css";
import renderDropdown from "../DropDownCustom";
import StationApi from "../../services/StationApi";
import DeviceApi from "../../services/DeviceApi";
import EventApi from "../../services/EventApi";
import { Device, Station } from "../../components/Filters/types/types";
import { Event } from "../../components/Chart/types/types";
import { useTranslation } from 'react-i18next'; 
import renderCalendar from "../Calendar";

interface FiltersProps {
    setChartData: React.Dispatch<React.SetStateAction<any[]>>;
    setDateFilter: React.Dispatch<React.SetStateAction<any[]>>;
    className?: string;
} 

const Filters = forwardRef<{}, FiltersProps>(({ setChartData, setDateFilter }, ref) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedStation, setSelectedStation] = useState<any>(null);
    const [selectedDeviceParam, setSelectedDeviceParam] = useState<any>(null);
    const [date, setDate] = useState<any>(new Date());
    const [stations, setStations] = useState<Station[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);
    const [filters, setFilters] = useState<{ stationId: number | null; deviceId: number | null, date: Date | null }>({
        stationId: null,
        deviceId: null,
        date: new Date()
    });
    const { t: translateGeneral } = useTranslation("general");
    const { t: translateForm } = useTranslation("form");

    useImperativeHandle(ref, () => ({
        handleFilterClick,
    }));

    const toggleFilters = (): void => {
        setIsOpen(prev => !prev);
    };

    const handleStationChange = async (e: { value: any }): Promise<void> => {
        setSelectedStation(e.value);
        setFilters(prev => ({ ...prev, stationId: e.value.id }));
        await fetchDevicesByStationId(e.value.id);
    };

    const handleDeviceChange = async (e: { value: any }): Promise<void> => {
        setSelectedDeviceParam(e.value);
        setFilters(prev => ({ ...prev, deviceId: e.value.id }));
    };

    const handleDateChange = async (e: { value: any }): Promise<void> => {  
        setDate(e.value);
        setDateFilter(e.value)
        setFilters(prev => ({ ...prev, date: e.value }));
    };

    const handleFilterClick = async (): Promise<void> => {
        if (filters.deviceId) {
            const events: Event[] = await fetchEventsByDeviceId(filters.deviceId, date);  
            setChartData(events);
        } else {
            console.log("Por favor, selecione um dispositivo.");
        }
    };

    // Fetch Stations
    useEffect(() => {
        const fetchStations = async (): Promise<void> => {
            try {
                const response = await StationApi.GetAllStations();
                const stationsData: Station[] = response.data.map((station: any) => ({
                    id: station.id,
                    name: station.name,
                    description: station.description,
                }));
                setStations(stationsData);
            } catch (error) {
                console.error("Erro ao buscar estações:", error);
            }
        };

        fetchStations();
    }, []);

    // Fetch Devices by Station
    const fetchDevicesByStationId = async (stationId: number): Promise<void> => {
        try {
            const response = await DeviceApi.GetDevicesByStationId(stationId);
            if (response.data && Array.isArray(response.data)) {
                const devicesData: Device[] = response.data.map((device: any) => ({
                    id: device.id,
                    name: device.name,
                    description: device.description,
                }));
                setDevices(devicesData);
            } else {
                console.error("Formato inesperado da resposta:", response.data);
            }
        } catch (error) {
            console.error("Erro ao buscar dispositivos:", error);
        }
    };

    const fetchEventsByDeviceId = async (deviceId: number, date: Date): Promise<any> => {
        try {  
            const response = await EventApi.GetEventsByDeviceId(deviceId, date);
            return response.data;
        } catch (error) {
            console.error("Erro ao buscar eventos:", error);
            return [];
        }
    };

    const stationOptions = stations.map(station => ({
        id: station.id,
        name: station.name,
        description: station.description
    }));

    const deviceOptions = devices.map(device => ({
        id: device.id,
        name: device.name,
        description: device.description
    }));

    return (
        <div className={styles.filtersContainer}>
            <Button
                onClick={toggleFilters}
                label={isOpen ? translateGeneral("collectFilter") : translateGeneral("filter")}
                className={`${styles.submit} ${styles.toggleButton}`}
            />
            {isOpen && (
                <div className={styles.filters}>
                    <>
                        {renderDropdown(stationOptions, translateForm("stations"), handleStationChange, selectedStation, false, false)}
                        {renderDropdown(deviceOptions, translateForm("devices"), handleDeviceChange, selectedDeviceParam, false, false)}
                        {renderCalendar(translateForm("date"), handleDateChange, date, false, "date", false)}
                        <Button
                            onClick={handleFilterClick}
                            label={translateGeneral("filter")}
                            className={`${styles.submit} ${styles.toggleButton}`}
                        />
                    </>
                </div>
            )}
        </div>
    );
});

export default Filters;
